import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    pollStatus(sessionId, 0);
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 8) { setStatus('timeout'); return; }
    try {
      const res = await fetch(`${API}/api/payments/checkout-status/${sid}`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setPaymentData(data);
      if (data.payment_status === 'paid') { setStatus('success'); return; }
      if (data.status === 'expired') { setStatus('expired'); return; }
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-success-page">
      <Navbar />
      <main className="vf-section">
        <div className="vf-container max-w-xl mx-auto text-center">
          {status === 'loading' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-20">
              <Loader2 className="w-16 h-16 animate-spin text-[#1e3a5f] mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">{t('pay_processing') || 'Processing payment...'}</h2>
              <p className="text-slate-500">{t('pay_wait') || 'Please wait while we confirm your payment.'}</p>
            </motion.div>
          )}

          {status === 'success' && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-20">
              <div className="w-20 h-20 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-[#22c55e]" />
              </div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-3">{t('pay_success') || 'Payment Successful!'}</h2>
              <p className="text-slate-500 mb-2">{t('pay_success_desc') || 'Your subscription is now active.'}</p>
              {paymentData?.metadata?.plan && (
                <p className="text-sm text-slate-400 mb-8">
                  {t('pay_plan') || 'Plan'}: <span className="font-semibold text-[#1e3a5f] capitalize">{paymentData.metadata.plan}</span>
                  {' · '}{paymentData.metadata.billing}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {paymentData?.metadata?.plan === 'pos' ? (
                  <Link to="/shop/dashboard" className="vf-btn-primary inline-flex items-center gap-2" data-testid="go-to-dashboard">
                    {t('pay_go_dashboard') || 'Go to Dashboard'} <ArrowRight className="w-5 h-5" />
                  </Link>
                ) : (
                  <Link to="/shop/invoices" className="vf-btn-primary inline-flex items-center gap-2" data-testid="go-to-invoices">
                    {t('pay_go_invoices') || 'Start Creating Invoices'} <ArrowRight className="w-5 h-5" />
                  </Link>
                )}
                <Link to={`/${language}`} className="vf-btn-secondary inline-flex items-center gap-2">
                  {t('pay_back_home') || 'Back to Home'}
                </Link>
              </div>
            </motion.div>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-20">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-3">{t('pay_error') || 'Payment Issue'}</h2>
              <p className="text-slate-500 mb-8">{t('pay_error_desc') || 'There was an issue processing your payment. Please try again.'}</p>
              <Link to={`/${language}/pricing`} className="vf-btn-primary inline-flex items-center gap-2">
                {t('pay_try_again') || 'Try Again'} <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
